import { configureStore } from '@reduxjs/toolkit';
import { useDispatch as useAppDispatch, useSelector as useAppSelector } from 'react-redux';
import { persistStore, persistReducer } from 'redux-persist';
import * as Sentry from "@sentry/react";
import { rootPersistConfig, rootReducer } from './rootReducer';
import { tikTokScopeMiddleware } from './slices/tiktokIntegration';

// ----------------------------------------------------------------------
const AUTH_ERROR_PATTERNS = [
    'token is invalid or expired',
    'token is blacklisted',
    'given token not valid',
    'authentication credentials were not provided',
    'session has timed out',
    'session will close in',
    'trying to extend session',
    'token_not_valid',
    'please login again',
];

const isAuthPayload = (payload) => {
    if (!payload) return false;
    const msg = (typeof payload === 'string' ? payload : JSON.stringify(payload)).toLowerCase();
    return AUTH_ERROR_PATTERNS.some((p) => msg.includes(p));
};

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
    actionTransformer: (action) => {
        // Default deny private inventory and all future TikTok action payloads.
        if (action.type?.startsWith('tiktokIntegration/')) return null;
        // Strip auth error payloads from Sentry breadcrumbs
        if (action.type === 'user/updateError' && isAuthPayload(action.payload?.message)) {
            return null; // Returning null drops the action from Sentry
        }
        return action;
    },
    stateTransformer: () => 
        // Don't send full Redux state to Sentry — it's huge and contains PII.
        // Auth state lives in React Context (JWTContext), not Redux, so we
        // return minimal info. Returning null disables state capture entirely.
         null
    ,
});

const store = configureStore({
    reducer: persistReducer(rootPersistConfig, rootReducer),
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: false,
            immutableCheck: false,
        }).concat(tikTokScopeMiddleware),
    enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(sentryReduxEnhancer),
});

const persistor = persistStore(store);

const { dispatch } = store;

const useSelector = useAppSelector;

const useDispatch = () => useAppDispatch();

export { store, persistor, dispatch, useSelector, useDispatch };
